import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Send, MapPin, Wallet, Check, Building2, BadgeCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { MatchScore, priceLabel } from "@/components/proptech";
import { api, apiError, peso, timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { propertyTypeLabel } from "@/lib/taxonomy";

export default function CollaborationRoom() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [stages, setStages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  const load = () => api.get(`/collaborations/${id}`).then((r) => setC(r.data)).catch((e) => setErr(apiError(e)));
  useEffect(() => { load(); api.get("/stages").then((r) => setStages(r.data)); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [c?.messages?.length]);

  const proById = (pid) => (c?.participant_profiles || []).find((p) => p?.id === pid);

  const send = async () => {
    if (!text.trim()) return;
    const t = text; setText("");
    try { await api.post(`/collaborations/${id}/messages`, { text: t }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const setStage = async (stage) => {
    try { await api.put(`/collaborations/${id}/stage`, { stage }); toast.success(`Stage updated: ${stage}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (err) return (<div className="min-h-screen bg-background"><Navbar /><div className="max-w-lg mx-auto mt-20 text-center"><p className="font-display text-xl font-semibold text-slate-800">{err}</p><Button onClick={() => nav("/collaborations")} className="mt-4 rounded-xl">Back to collaborations</Button></div></div>);
  if (!c) return (<div className="min-h-screen bg-background"><Navbar /><p className="p-8 text-slate-500">Loading room…</p></div>);

  const stageIdx = stages.indexOf(c.stage);
  const ps = c.property_snapshot, bs = c.buyer_snapshot;
  const isRecipient = c.recipient_id === user?.id;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-4 flex-1 flex flex-col" data-testid="collaboration-room">
        <button onClick={() => nav("/collaborations")} data-testid="back-btn" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3"><ArrowLeft className="h-4 w-4" /> All collaborations</button>

        {isRecipient && c.stage === "Requested" && (
          <div className="mb-3 flex items-center justify-between gap-3 bg-accent/10 border border-accent/30 rounded-xl px-4 py-3">
            <span className="text-sm text-slate-700 font-medium">You've received a collaboration request on a {c.match_score}% match.</span>
            <Button data-testid="accept-collab" onClick={() => setStage("Accepted")} className="bg-accent text-accent-foreground rounded-xl">Accept request</Button>
          </div>
        )}

        <div className="grid lg:grid-cols-[40%_60%] gap-5 flex-1 min-h-0">
          <div className="space-y-4 overflow-y-auto pc-scroll pr-1">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="relative h-40"><img src={ps?.image} alt="" className="h-full w-full object-cover" /><div className="absolute top-2 right-2 bg-white rounded-full p-0.5 shadow"><MatchScore score={c.match_score} size={52} /></div></div>
              <div className="p-4">
                <span className="text-[11px] font-semibold uppercase tracking-wide bg-secondary text-slate-600 px-2 py-0.5 rounded">{ps?.listing_type}</span>
                <h2 className="font-display text-lg font-semibold text-slate-900 mt-2">{ps?.title}</h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500"><span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{ps?.neighborhood}, {ps?.city}</span><span>{ps?.bedrooms} BR · {propertyTypeLabel(ps?.property_type)}</span></div>
                <div className="font-display text-xl font-bold text-slate-900 mt-1">{priceLabel(ps)}</div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <p className="eyebrow mb-2">Buyer</p>
              <div className="font-semibold text-slate-900">{bs?.buyer_alias}</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-500"><span className="flex items-center gap-1"><Wallet className="h-4 w-4" />{peso(bs?.budget_min)}–{peso(bs?.budget_max)}</span><span>{propertyTypeLabel(bs?.property_type)}</span></div>
              {bs?.notes && <p className="text-sm text-slate-500 mt-2">{bs.notes}</p>}
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <p className="eyebrow mb-3">Participants</p>
              <div className="space-y-3">
                {(c.participant_profiles || []).filter(Boolean).map((p) => (
                  <div key={p.id} className="flex items-center gap-2">
                    <img src={p.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
                    <div className="leading-tight"><div className="text-sm font-medium text-slate-700 flex items-center gap-1">{p.name}{p.verification_status === "PROFESSIONAL_VERIFIED" && <BadgeCheck className="h-3.5 w-3.5 text-accent" />}{p.id === user?.id && <span className="text-[10px] text-slate-400">(you)</span>}</div><div className="text-[11px] text-slate-500">{p.agency}</div></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-4">
              <p className="eyebrow mb-3">Deal progress</p>
              <div className="space-y-1.5" data-testid="stage-tracker">
                {stages.map((s, i) => {
                  const done = i <= stageIdx; const isNext = i === stageIdx + 1;
                  return (
                    <button key={s} data-testid={`stage-${s.replace(/\s/g, "-")}`} onClick={() => setStage(s)} className={`w-full flex items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${done ? "text-slate-900" : "text-slate-400 hover:bg-secondary"}`}>
                      <span className={`h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 ${done ? "bg-accent text-accent-foreground" : isNext ? "border-2 border-accent text-accent" : "border border-border"}`}>{done ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
                      <span className="text-sm font-medium">{s}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl flex flex-col min-h-[60vh] lg:min-h-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-background/80 backdrop-blur flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" /><span className="font-display font-semibold text-slate-900">Deal Room</span><span className="text-xs text-slate-400 ml-auto">Started {timeAgo(c.created_at)}</span></div>
            <div className="flex-1 overflow-y-auto pc-scroll p-5 space-y-4" data-testid="message-thread">
              {c.messages.length === 0 && <p className="text-center text-slate-400 text-sm mt-8">No messages yet. Say hello 👋</p>}
              {c.messages.map((m) => {
                const mine = m.sender_id === user?.id; const sender = proById(m.sender_id);
                return (
                  <div key={m.id} className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}>
                    <img src={sender?.avatar} alt="" className="h-8 w-8 rounded-full object-cover shrink-0" />
                    <div className={`max-w-[75%] ${mine ? "items-end" : ""} flex flex-col`}>
                      <div className={`rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary text-slate-800 rounded-tl-sm"}`}>{m.text}</div>
                      <span className="text-[10px] text-slate-400 mt-1 px-1">{sender?.name?.split(" ")[0]} · {timeAgo(m.created_at)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-border flex gap-2">
              <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Write a message…" className="flex-1 rounded-xl border border-input bg-background px-4 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" />
              <Button data-testid="send-message" onClick={send} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl px-4 active:scale-95 transition-transform"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
